import React from "react";
import { classNames } from "../../../helpers";
import { useToast } from "../../../provider/ToastProvider";
import axiosInstance from "../../../utils/axiosInstance";
import { getUrlForBookings, getUrlForClasses } from "../../../utils/confighelpers";
import { FcModal, FieldError, FieldLabel, inputClass } from "../schedule/ui";
import { apiResult, euro, fmtDate, purchaseName, REFUND_REASONS } from "./memberUtils";

/**
 * Pop-ups on the member page (new design), same calls as before:
 *  CancelSubscriptionModal → POST bookings/getStripeInvoicePreview {purchase_id}, POST bookings/cancelProductSubscription {purchase_id, cancelType, refund}
 *  RemovePassModal         → POST bookings/removeProduct {purchase_id, productType:"classpass", refund, customerId, stripeCustomer, refundAmount, reason, reasonDetail}
 *  RefundModal             → POST bookings/refundPayment {...purchase_detail, stripeCustomer, customerId, refundAmount, reason, reasonDetail}
 *  GiveCreditsModal        → POST bookings/purchaseClassPass (migration credit, paymentStatus Paid/Free)
 */

const ErrorBox = ({ children }) =>
  children ? (
    <div className="rounded-2xl border border-red-200 bg-red-50 px-4 py-3 text-sm font-semibold text-red-700" role="alert">
      {children}
    </div>
  ) : null;

export function Segmented({ options, value, onChange }) {
  return (
    <div className="flex gap-1 rounded-2xl bg-[#F3EEF5] p-1">
      {options.map((o) => (
        <button
          key={o.value}
          type="button"
          aria-pressed={value === o.value}
          onClick={() => onChange(o.value)}
          className={classNames("min-h-9 flex-1 rounded-xl px-3 py-1.5 text-sm font-extrabold", value === o.value ? "bg-white text-brown-coffee shadow-sm" : "text-gray-500 hover:text-gray-900")}
        >
          {o.label}
        </button>
      ))}
    </div>
  );
}

export function RadioCards({ options, value, onChange, label }) {
  return (
    <div role="radiogroup" aria-label={label} className="flex flex-col gap-2">
      {options.map((o) => {
        const on = value === o.value;
        return (
          <button
            key={o.value}
            type="button"
            role="radio"
            aria-checked={on}
            disabled={o.disabled}
            onClick={() => onChange(o.value)}
            className={classNames("flex w-full items-center gap-3 rounded-2xl border p-3.5 text-left transition-colors disabled:opacity-50", on ? "border-indigo-bz bg-[#FBF8FE] ring-2 ring-[#F3E9FF]" : "border-gray-200 bg-white hover:bg-gray-50")}
          >
            <span className="min-w-0 flex-1">
              <span className="flex flex-wrap items-center gap-2 text-[15px] font-extrabold text-gray-900">
                {o.label}
                {o.tag}
              </span>
              {o.sub && <span className="mt-0.5 block text-[13px] text-gray-500">{o.sub}</span>}
            </span>
            {o.right && <span className="shrink-0 text-[15px] font-black text-gray-900">{o.right}</span>}
            <span className={classNames("flex h-5 w-5 shrink-0 items-center justify-center rounded-full border-2", on ? "border-indigo-bz" : "border-gray-300")}>
              {on && <span className="h-2.5 w-2.5 rounded-full bg-indigo-bz" />}
            </span>
          </button>
        );
      })}
    </div>
  );
}

function ReasonFields({ reason, setReason, note, setNote, errors }) {
  return (
    <div className="grid gap-3 sm:grid-cols-2">
      <div>
        <FieldLabel htmlFor="fc-reason">Reason</FieldLabel>
        <select id="fc-reason" value={reason} onChange={(e) => setReason(e.target.value)} className={inputClass(errors.reason)}>
          <option value="">Choose a reason…</option>
          {REFUND_REASONS.map((r) => (
            <option key={r.value} value={r.value}>{r.label}</option>
          ))}
        </select>
        <FieldError>{errors.reason}</FieldError>
      </div>
      <div>
        <FieldLabel htmlFor="fc-note" optional={reason !== "other"}>Note</FieldLabel>
        <input id="fc-note" value={note} onChange={(e) => setNote(e.target.value)} placeholder="e.g. Moving abroad" className={inputClass(errors.note)} />
        <FieldError>{errors.note}</FieldError>
      </div>
    </div>
  );
}

export function CancelSubscriptionModal({ open, onClose, purchase, memberFirst, onDone = () => null }) {
  const { addSuccessToast } = useToast();
  const [when, setWhen] = React.useState("end_of_the_current_period");
  const [refund, setRefund] = React.useState("no_refund");
  const [prorated, setProrated] = React.useState(null);
  const [busy, setBusy] = React.useState(false);
  const [error, setError] = React.useState("");
  const d = purchase?.selectedPassDetail || {};

  React.useEffect(() => {
    if (!open || !purchase) return;
    setWhen("end_of_the_current_period");
    setRefund("no_refund");
    setProrated(null);
    setError("");
    setBusy(false);
    if (purchase.paymentStatus !== "Paid") return;
    (async () => {
      try {
        const res = await axiosInstance({ method: "post", url: `${getUrlForBookings()}getStripeInvoicePreview`, data: { purchase_id: purchase._id } });
        const [ok, data] = apiResult(res);
        if (ok && data?.id && data?.latestInvoice?.paid === true) setProrated(data.total < 0 ? -data.total / 100 : 0);
      } catch (e) {
        /* no pro-rated option */
      }
    })();
  }, [open, purchase]);

  const save = async () => {
    setBusy(true);
    setError("");
    try {
      const res = await axiosInstance({
        method: "post",
        url: `${getUrlForBookings("")}cancelProductSubscription`,
        data: { purchase_id: purchase._id, cancelType: when, refund: when === "immediately" ? refund : "no_refund" },
      });
      const [ok, , msg] = apiResult(res);
      if (ok) {
        addSuccessToast(when === "immediately" ? "Subscription cancelled" : `Subscription ends on ${fmtDate(purchase.purchaseExpiredDate)}`);
        onDone();
        onClose();
      } else setError(msg || "Couldn't cancel this subscription.");
    } catch (e) {
      setError("Couldn't reach the server. Please try again.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <FcModal
      open={open}
      onClose={onClose}
      eyebrow="CANCEL SUBSCRIPTION"
      title={`Cancel ${purchaseName(purchase)}?`}
      subtitle={`Bookings ${memberFirst || "they"} already made stay.`}
      primaryLabel="Cancel subscription"
      danger
      onPrimary={save}
      busy={busy}
    >
      <ErrorBox>{error}</ErrorBox>
      <div>
        <FieldLabel>When should it stop?</FieldLabel>
        <RadioCards
          label="When"
          value={when}
          onChange={setWhen}
          options={[
            { value: "end_of_the_current_period", label: "At the end of this period", sub: `Stops on ${fmtDate(purchase?.purchaseExpiredDate)}. They can keep booking until then.` },
            { value: "immediately", label: "Right away", sub: `Stops today, ${fmtDate(new Date())}.` },
          ]}
        />
      </div>
      {when === "immediately" && (
        <div>
          <FieldLabel>Refund</FieldLabel>
          <Segmented
            value={refund}
            onChange={setRefund}
            options={[{ value: "no_refund", label: "No refund" }, ...(prorated != null ? [{ value: "pro_rated_amount", label: `Pro-rated ${euro(prorated)} of ${euro(d.price)}` }] : [])]}
          />
          {prorated == null && <p className="mt-2 text-xs text-gray-500">A pro-rated refund is only possible when their last invoice was paid online.</p>}
        </div>
      )}
    </FcModal>
  );
}

export function RemovePassModal({ open, onClose, purchase, consumerId, stripeCustomer, memberFirst, onDone = () => null }) {
  const { addSuccessToast } = useToast();
  const [refund, setRefund] = React.useState("no_refund");
  const [amount, setAmount] = React.useState("");
  const [reason, setReason] = React.useState("");
  const [note, setNote] = React.useState("");
  const [errors, setErrors] = React.useState({});
  const [busy, setBusy] = React.useState(false);
  const paid = parseFloat(purchase?.totalStripeAmount || purchase?.selectedPassDetail?.price || 0);
  const canRefund = purchase?.paymentMethod !== "Cash";
  const left = parseInt(purchase?.total_voucher_unused, 10) || 0;

  React.useEffect(() => {
    if (!open) return;
    setRefund("no_refund");
    setAmount(paid ? paid.toFixed(2) : "");
    setReason("");
    setNote("");
    setErrors({});
    setBusy(false);
  }, [open, paid]);

  const save = async () => {
    const e = {};
    const amt = parseFloat(String(amount).replace(",", "."));
    if (refund === "with_refund") {
      if (!(amt > 0)) e.amount = "Enter an amount above €0.";
      else if (amt > paid) e.amount = `Refund can't be more than ${euro(paid)}.`;
    }
    if (!reason) e.reason = "Choose a reason.";
    if (reason === "other" && !note.trim()) e.note = "Add a few words.";
    setErrors(e);
    if (Object.keys(e).length) return;
    setBusy(true);
    try {
      const res = await axiosInstance({
        method: "post",
        url: `${getUrlForBookings("")}removeProduct`,
        data: {
          purchase_id: purchase._id,
          productType: "classpass",
          refund,
          customerId: consumerId,
          stripeCustomer,
          refundAmount: refund === "with_refund" ? amt : 0,
          reason,
          reasonDetail: note.trim() || reason,
        },
      });
      const [ok, , msg] = apiResult(res);
      if (ok) {
        addSuccessToast(refund === "with_refund" ? `Pass removed · ${euro(amt)} refund started` : "Pass removed");
        onDone();
        onClose();
      } else setErrors({ form: msg || "Couldn't remove this pass." });
    } catch (err) {
      setErrors({ form: "Couldn't reach the server. Please try again." });
    } finally {
      setBusy(false);
    }
  };

  return (
    <FcModal
      open={open}
      onClose={onClose}
      eyebrow="REMOVE PASS"
      title={`Remove ${purchaseName(purchase)}?`}
      subtitle={`${memberFirst || "They"} can't book with it any more.${left ? ` ${left} unused ${left === 1 ? "credit is" : "credits are"} lost.` : ""}`}
      primaryLabel={refund === "with_refund" ? `Remove and refund` : "Remove pass"}
      danger
      onPrimary={save}
      busy={busy}
    >
      <ErrorBox>{errors.form}</ErrorBox>
      {canRefund ? (
        <Segmented value={refund} onChange={setRefund} options={[{ value: "no_refund", label: "Remove, no refund" }, { value: "with_refund", label: "Remove and refund" }]} />
      ) : (
        <p className="rounded-2xl bg-[#F7F4F8] px-4 py-3 text-sm text-gray-600">This pass was paid in cash, so there's nothing to refund online.</p>
      )}
      {refund === "with_refund" && (
        <div className="sm:max-w-[240px]">
          <FieldLabel htmlFor="rp-amount">Refund amount</FieldLabel>
          <input id="rp-amount" inputMode="decimal" value={amount} onChange={(e) => setAmount(e.target.value)} className={inputClass(errors.amount)} />
          <p className="mt-1 text-xs text-gray-500">Paid {euro(paid)}</p>
          <FieldError>{errors.amount}</FieldError>
        </div>
      )}
      <ReasonFields reason={reason} setReason={setReason} note={note} setNote={setNote} errors={errors} />
    </FcModal>
  );
}

export function RefundModal({ open, onClose, payment, item, consumerId, onDone = () => null }) {
  const { addSuccessToast } = useToast();
  const [mode, setMode] = React.useState("full");
  const [amount, setAmount] = React.useState("");
  const [reason, setReason] = React.useState("");
  const [note, setNote] = React.useState("");
  const [errors, setErrors] = React.useState({});
  const [busy, setBusy] = React.useState(false);
  const refundedBefore = (payment?.payment_intent?.charges?.data?.[0]?.amount_refunded || 0) / 100;
  const max = Math.max(0, (parseFloat(payment?.amount_total) || 0) - refundedBefore);

  React.useEffect(() => {
    if (!open) return;
    setMode("full");
    setAmount(max.toFixed(2));
    setReason("");
    setNote("");
    setErrors({});
    setBusy(false);
  }, [open, max]);

  const save = async () => {
    const e = {};
    const amt = mode === "full" ? max : parseFloat(String(amount).replace(",", "."));
    if (!(amt > 0)) e.amount = "Enter an amount above €0.";
    else if (amt > max) e.amount = `Refund can't be more than ${euro(max)}.`;
    if (!reason) e.reason = "Choose a reason.";
    if (reason === "other" && !note.trim()) e.note = "Add a few words.";
    setErrors(e);
    if (Object.keys(e).length) return;
    setBusy(true);
    try {
      const res = await axiosInstance({
        method: "post",
        url: `${getUrlForBookings("")}refundPayment`,
        data: {
          ...(payment.purchase_detail || {}),
          stripeCustomer: payment.customer_id,
          customerId: consumerId,
          refundAmount: amt,
          reason,
          reasonDetail: note.trim() || reason,
        },
      });
      const [ok, , msg] = apiResult(res);
      if (ok) {
        addSuccessToast(`Refund of ${euro(amt)} started`);
        onDone();
        onClose();
      } else setErrors({ form: msg || "Couldn't refund this payment." });
    } catch (err) {
      setErrors({ form: "Couldn't reach the server. Please try again." });
    } finally {
      setBusy(false);
    }
  };

  return (
    <FcModal
      open={open}
      onClose={onClose}
      eyebrow="REFUND"
      title={`Refund ${item || "payment"}`}
      subtitle={`Paid ${euro(payment?.amount_total)} on ${fmtDate(payment?.created)}. The money goes back to the way they paid, usually within 5–10 days.`}
      primaryLabel={`Refund ${euro(mode === "full" ? max : parseFloat(String(amount).replace(",", ".")) || 0)}`}
      danger
      onPrimary={save}
      busy={busy}
    >
      <ErrorBox>{errors.form}</ErrorBox>
      <Segmented value={mode} onChange={setMode} options={[{ value: "full", label: refundedBefore ? `The rest (${euro(max)})` : "Full refund" }, { value: "partial", label: "Part of it" }]} />
      {mode === "partial" && (
        <div className="sm:max-w-[240px]">
          <FieldLabel htmlFor="rf-amount">Amount to refund</FieldLabel>
          <input id="rf-amount" inputMode="decimal" value={amount} onChange={(e) => setAmount(e.target.value)} className={inputClass(errors.amount)} />
          <FieldError>{errors.amount}</FieldError>
        </div>
      )}
      <ReasonFields reason={reason} setReason={setReason} note={note} setNote={setNote} errors={errors} />
    </FcModal>
  );
}

export function GiveCreditsModal({ open, onClose, consumer, onDone = () => null }) {
  const { addSuccessToast } = useToast();
  const [passes, setPasses] = React.useState([]);
  const [passId, setPassId] = React.useState("");
  const [credits, setCredits] = React.useState(1);
  const [error, setError] = React.useState("");
  const [busy, setBusy] = React.useState(false);
  const first = (consumer?.fullName || "").split(" ")[0] || "them";

  React.useEffect(() => {
    if (!open) return;
    setCredits(1);
    setError("");
    setBusy(false);
    (async () => {
      try {
        const res = await axiosInstance({ method: "get", url: `${getUrlForClasses("")}products/list?getall=false&productType=classpass` });
        const [ok, data] = apiResult(res);
        const list = ok && Array.isArray(data) ? data.filter((p) => p.productName !== "free trial credit" && p.productType === "classpass") : [];
        list.sort((a, b) => (parseFloat(a.price) > 0) - (parseFloat(b.price) > 0));
        setPasses(list);
        setPassId(list[0]?._id || "");
      } catch (e) {
        setPasses([]);
      }
    })();
  }, [open]);

  const pass = passes.find((p) => p._id === passId);
  const max = parseInt(pass?.productFields?.noOfAllowedSessions, 10) || 99;

  const save = async () => {
    if (!pass) return setError("Pick a pass.");
    if (credits < 1 || credits > max) return setError(`Give between 1 and ${max} credits on this pass.`);
    setBusy(true);
    setError("");
    try {
      const res = await axiosInstance({
        method: "post",
        url: `${getUrlForBookings()}purchaseClassPass`,
        data: {
          productId: pass._id,
          migrationCredit: credits,
          username: consumer?.username || "",
          fullName: consumer?.fullName || "",
          email: consumer?.email || "",
          purchaseId: "",
          paymentMethod: "NA",
          paymentMode: "NA",
          paymentStatus: parseFloat(pass.price) > 0 ? "Free" : "Paid",
        },
      });
      const [ok, , msg] = apiResult(res);
      if (ok) {
        addSuccessToast(`${credits} ${credits === 1 ? "credit" : "credits"} added for ${first}`);
        onDone();
        onClose();
      } else setError(msg || "Couldn't add these credits.");
    } catch (e) {
      setError("Couldn't reach the server. Please try again.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <FcModal
      open={open}
      onClose={onClose}
      eyebrow="MIGRATION CREDIT"
      title={`Move ${first}'s credits over`}
      subtitle="Pick the same pass they had in your old system and the credits they have left. Nothing is charged."
      primaryLabel={`Add ${credits} ${credits === 1 ? "credit" : "credits"}`}
      onPrimary={save}
      busy={busy}
      primaryDisabled={!pass}
    >
      <ErrorBox>{error}</ErrorBox>
      <div>
        <FieldLabel htmlFor="gc-pass">Pass</FieldLabel>
        {passes.length ? (
          <select id="gc-pass" value={passId} onChange={(e) => setPassId(e.target.value)} className={inputClass(false)}>
            {passes.map((p) => (
              <option key={p._id} value={p._id}>
                {p.productName}{parseFloat(p.price) > 0 ? ` (normally ${euro(p.price)})` : ""}
              </option>
            ))}
          </select>
        ) : (
          <p className="rounded-2xl bg-[#FFF4E5] px-4 py-3 text-sm font-semibold text-[#7A4A00]">You have no credit pass yet. Create one under My Products first.</p>
        )}
        <p className="mt-1.5 text-xs text-gray-500">The pass rules (which classes, how long it's valid) apply to these credits.</p>
      </div>
      <div>
        <FieldLabel>Credits left</FieldLabel>
        <div className="flex items-center gap-3">
          <button type="button" aria-label="Fewer credits" onClick={() => setCredits((c) => Math.max(1, c - 1))} className="flex h-12 w-12 items-center justify-center rounded-2xl border border-gray-200 text-2xl font-extrabold text-gray-700 hover:bg-gray-50">−</button>
          <span className="w-16 text-center text-3xl font-black text-gray-900" aria-live="polite">{credits}</span>
          <button type="button" aria-label="More credits" onClick={() => setCredits((c) => Math.min(max, c + 1))} className="flex h-12 w-12 items-center justify-center rounded-2xl border border-gray-200 text-2xl font-extrabold text-gray-700 hover:bg-gray-50">+</button>
          <span className="text-xs text-gray-500">max {max}</span>
        </div>
      </div>
    </FcModal>
  );
}

/**
 * Free trial credit (member page, Free credits): 1 credit on the club's "free trial credit" product, same request as the
 * old PurchasesFreeTrial: POST bookings/purchaseClassPass {productId, migrationCredit: 1, paymentStatus "Paid"} (price 0,
 * so the backend books it as Free). An active free trial for this member gets +1 credit instead of a new one.
 */
export function FreeTrialCreditModal({ open, onClose, consumer, onDone = () => null }) {
  const { addSuccessToast } = useToast();
  const [product, setProduct] = React.useState(null);
  const [loading, setLoading] = React.useState(false);
  const [error, setError] = React.useState("");
  const [busy, setBusy] = React.useState(false);
  const first = (consumer?.fullName || "").split(" ")[0] || "them";

  React.useEffect(() => {
    if (!open) return;
    setError("");
    setBusy(false);
    setLoading(true);
    (async () => {
      try {
        const res = await axiosInstance({ method: "get", url: `${getUrlForClasses("")}products/list?getall=false&productType=classpass` });
        const [ok, data] = apiResult(res);
        setProduct(ok && Array.isArray(data) ? data.find((p) => p.productName === "free trial credit") || null : null);
      } catch (e) {
        setProduct(null);
      } finally {
        setLoading(false);
      }
    })();
  }, [open]);

  const save = async () => {
    if (!product) return;
    setBusy(true);
    setError("");
    try {
      const res = await axiosInstance({
        method: "post",
        url: `${getUrlForBookings()}purchaseClassPass`,
        data: {
          productId: product._id,
          migrationCredit: 1,
          username: consumer?.username || "",
          fullName: consumer?.fullName || "",
          email: consumer?.email || "",
          purchaseId: "",
          paymentMethod: "NA",
          paymentMode: "NA",
          paymentStatus: "Paid",
        },
      });
      const [ok, , msg] = apiResult(res);
      if (ok) {
        addSuccessToast(`Free trial credit added for ${first}`);
        onDone();
        onClose();
      } else setError(msg || "Couldn't add the free trial credit.");
    } catch (e) {
      setError("Couldn't reach the server. Please try again.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <FcModal
      open={open}
      onClose={onClose}
      eyebrow="FREE TRIAL CREDIT"
      title={`Give ${first} a free trial class?`}
      subtitle="They get 1 credit to book any class. Nothing is charged."
      primaryLabel="Add 1 credit"
      onPrimary={save}
      busy={busy}
      primaryDisabled={loading || !product}
    >
      <ErrorBox>{error}</ErrorBox>
      {!loading && !product && (
        <p className="rounded-2xl bg-[#FFF4E5] px-4 py-3 text-sm font-semibold text-[#7A4A00]">Your club has no free trial credit set up. Contact FitClub support to turn it on.</p>
      )}
      {(loading || product) && (
        <p className="text-sm text-gray-600">If {first} already has an unused free trial, the credit is added to it.</p>
      )}
    </FcModal>
  );
}
